import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  SparklesIcon,
  BookOpenIcon,
  ClipboardListIcon,
  TargetIcon,
  CheckCircleIcon,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Navbar } from "../../components/common/Navbar";
import { useAuth } from "../../context/AuthContext";
import {
  getUserChallenges,
  UserChallenge,
  updateChallengeProgress,
} from "../../lib/supabase";
import toast from "react-hot-toast";

export const GamificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState<UserChallenge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadChallenges();
    }
  }, [user?.id]);

  const loadChallenges = async () => {
    if (!user?.id) return;

    setLoading(true);
    const result = await getUserChallenges(user.id);

    if (result.success && result.data) {
      setChallenges(result.data);
    } else if (result.error) {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleGoToTask = async (challengeType: string) => {
    switch (challengeType) {
      case "lesson_plan":
        navigate("/lesson-plan-builder");
        break;
      case "assessment":
        navigate("/assessment-builder");
        break;
      case "share_result":
        if (!user?.id) {
          toast.error("User tidak ditemukan");
          return;
        }

        try {
          const result = await updateChallengeProgress(user.id, "share_result");

          if (result.success) {
            toast.success("Progress tantangan diperbarui!");
            if (
              result.data?.challenge_completed &&
              result.data.points_awarded > 0
            ) {
              toast.success(`+${result.data.points_awarded} Poin!`);
            }
            loadChallenges();
          } else {
            toast.error(result.error || "Gagal memperbarui tantangan.");
          }
          navigate("/lesson-plan-builder");
        } catch (error) {
          toast.error("Terjadi kesalahan.");
          console.error("Gagal update challenge:", error);
        }
        break;

      default:
        toast.info("Fitur ini akan segera tersedia");
    }
  };

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (user?.profile?.avatar_url) {
      return user.profile.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  // Get challenge icon based on type
  const getChallengeIcon = (challengeType: string) => {
    switch (challengeType) {
      case "lesson_plan":
        return BookOpenIcon;
      case "assessment":
        return ClipboardListIcon;
      case "share_result":
        return BookOpenIcon;
      case "enhance_prompt":
        return SparklesIcon;
      default:
        return TargetIcon;
    }
  };

  // Get challenge icon color based on type
  const getChallengeIconColor = (challengeType: string) => {
    switch (challengeType) {
      case "lesson_plan":
        return "bg-blue-100 text-blue-600";
      case "assessment":
        return "bg-green-100 text-green-600";
      case "share_result":
        return "bg-blue-100 text-blue-600";
      case "enhance_prompt":
        return "bg-purple-100 text-purple-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Main Content with top padding to account for fixed navbar */}
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full max-w-[1392px] mx-auto mt-4 sm:mt-8 bg-white rounded-[21px] border-b border-[#f0eff0] p-4 sm:p-8 min-h-[700px]">
          <div className="max-w-4xl mx-auto">
            {/* Header with back button */}
            <div className="flex items-center gap-4 mb-6 sm:mb-8">
              <Button
                onClick={handleBackToDashboard}
                variant="ghost"
                className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Button>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#373e46]">
                Point & Challenge
              </h2>
            </div>

            {/* User Profile Section */}
            <div className="bg-gradient-to-r from-[#f8f9ff] to-[#f3f4ff] rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-[#e3e6eb]">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                {/* Avatar */}
                <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-lg">
                  <AvatarFallback>
                    <img
                      className="w-full h-full object-cover"
                      alt="User Avatar"
                      src={getAvatarUrl()}
                    />
                  </AvatarFallback>
                </Avatar>

                {/* User Info */}
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#373e46] mb-1">
                    {user?.profile?.name || "Nama guru"}
                  </h3>
                  <p className="text-[#373e46] mb-3 flex items-center justify-center sm:justify-start gap-2">
                    <span className="text-sm">🏫</span>
                    {user?.profile?.asal_sekolah || "Asal sekolah"}
                  </p>

                  {/* Points Badge */}
                  <Badge className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white h-10 px-6 flex items-center gap-2 rounded-full text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 w-fit mx-auto sm:mx-0">
                    <img
                      className="w-5 h-5"
                      alt="Medal icon"
                      src="/medal.png"
                    />
                    <span>{user?.profile?.points || 0} Point</span>
                  </Badge>
                </div>
              </div>
            </div>

            {/* Daily Challenge Section */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <TargetIcon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-[#373e46]">
                  Daily Challenge
                </h3>
              </div>

              {loading ? (
                /* Loading State */
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="bg-white rounded-xl p-4 border border-gray-200 animate-pulse"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                        </div>
                        <div className="w-20 h-8 bg-gray-200 rounded-lg"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : challenges.length > 0 ? (
                /* Challenges List */
                <div className="space-y-4">
                  {challenges.map(challenge => {
                    const Icon = getChallengeIcon(
                      challenge.challenge_types?.challenge_type || ""
                    );
                    const iconColorClass = getChallengeIconColor(
                      challenge.challenge_types?.challenge_type || ""
                    );
                    const progressPercentage =
                      (challenge.current_progress / challenge.target_count) *
                      100;

                    return (
                      <div
                        key={challenge.id}
                        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center gap-4">
                          {/* Challenge Icon */}
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center ${iconColorClass}`}
                          >
                            <Icon className="w-6 h-6" />
                          </div>

                          {/* Challenge Info */}
                          <div className="flex-1">
                            <h4 className="font-semibold text-[#373e46] mb-1">
                              {challenge.challenge_types?.name || "Challenge"}
                            </h4>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-yellow-100 text-yellow-800 h-6 px-2 flex items-center gap-1 rounded-full text-xs font-medium hover:bg-yellow-100 hover:text-yellow-800 pointer-events-none">
                                <span>{challenge.points_reward} Point</span>
                              </Badge>
                              {challenge.is_completed && (
                                <Badge className="bg-green-100 text-green-800 h-6 px-2 flex items-center gap-1 rounded-full text-xs font-medium hover:bg-green-100 hover:text-green-800 pointer-events-none">
                                  <CheckCircleIcon className="w-3 h-3" />
                                  <span>Selesai</span>
                                </Badge>
                              )}
                            </div>

                            {/* Progress Bar */}
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    challenge.is_completed
                                      ? "bg-green-500"
                                      : "bg-blue-500"
                                  }`}
                                  style={{
                                    width: `${Math.min(progressPercentage, 100)}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 font-medium min-w-[60px]">
                                {challenge.current_progress}/
                                {challenge.target_count}
                              </span>
                            </div>
                          </div>

                          {/* Action Button */}
                          {challenge.challenge_types?.challenge_type !==
                            "enhance_prompt" && (
                            <Button
                              onClick={() =>
                                handleGoToTask(
                                  challenge.challenge_types?.challenge_type ||
                                    ""
                                )
                              }
                              disabled={challenge.is_completed}
                              className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 text-sm ${
                                challenge.is_completed
                                  ? "bg-green-100 text-green-800 cursor-not-allowed"
                                  : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
                              }`}
                            >
                              {challenge.is_completed ? (
                                <div className="flex items-center gap-2">
                                  <CheckCircleIcon className="w-4 h-4" />
                                  <span>Selesai</span>
                                </div>
                              ) : (
                                "Go to task"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Empty State */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TargetIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-[#373e46] mb-2">
                    Belum Ada Tantangan
                  </h4>
                  <p className="text-gray-500 mb-4">
                    Tantangan harian akan muncul di sini
                  </p>
                  <Button
                    onClick={loadChallenges}
                    className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
                  >
                    Muat Ulang
                  </Button>
                </div>
              )}
            </div>

            {/* Tips Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 mt-6 sm:mt-8">
              <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5" />
                Tips Mendapatkan Poin
              </h3>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>
                  • Selesaikan tantangan harian untuk mendapatkan poin bonus
                </li>
                <li>
                  • Buat rencana pembelajaran dan soal assessment secara rutin
                </li>
                <li>
                  • Gunakan fitur Enhance Prompt untuk meningkatkan kualitas
                  konten
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
